import { useState, useContext, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Tabs, Tab, Typography, Button, alpha } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import BusinessIcon from '@mui/icons-material/Business';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { CreateB2BSale } from './CreateB2BSale';
import { UnifiedSalesHistory } from './UnifiedSalesHistory';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`b2b-sales-tabpanel-${index}`}
      aria-labelledby={`b2b-sales-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const B2BSalesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  // Get tab from URL, default to 'create'
  const tabParam = searchParams.get('tab');
  const getTabIndex = (tab: string | null): number => {
    switch (tab) {
      case 'history':
        return 1;
      default:
        return 0;
    }
  };

  const [tabValue, setTabValue] = useState(getTabIndex(tabParam));

  // Update tab when URL changes
  useEffect(() => {
    setTabValue(getTabIndex(tabParam));
  }, [tabParam]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const tabNames = ['create', 'history'];
    setSearchParams({ tab: tabNames[newValue] });
    setTabValue(newValue);
  };

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Industrial Header */}
      <Box sx={{ mb: 3, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BusinessIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                B2B SALES SYSTEM
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              WHOLESALE SALES
            </Typography>
            <Typography
              sx={{
                mt: 1,
                fontFamily: typography.fontFamily.body,
                fontSize: typography.fontSize.sm,
                color: colors.textMuted,
              }}
            >
              Record direct B2B sales with automatic tier pricing and quantity discounts
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/app/sales-b2b/create')}
            sx={{
              backgroundColor: palette.primary,
              color: '#FFFFFF',
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.8),
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: `0 8px 24px ${alpha(palette.primary, 0.3)}`,
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            New B2B Sale
          </Button>
        </Box>
      </Box>

      {/* Tabs Header */}
      <Box
        sx={{
          mb: 3,
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.1),
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="B2B Sales tabs"
          sx={{
            minHeight: 56,
            '& .MuiTabs-indicator': {
              backgroundColor: palette.primary,
              height: 3,
            },
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'uppercase',
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              letterSpacing: typography.letterSpacing.widest,
              color: colors.textMuted,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.05),
                color: colors.textPrimary,
              },
              '&.Mui-selected': {
                color: palette.primary,
                backgroundColor: alpha(palette.primary, 0.1),
              },
            },
          }}
        >
          <Tab
            icon={<StorefrontIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Create Sale"
            id="b2b-sales-tab-0"
            aria-controls="b2b-sales-tabpanel-0"
          />
          <Tab
            icon={<HistoryIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Sales History"
            id="b2b-sales-tab-1"
            aria-controls="b2b-sales-tabpanel-1"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <CreateB2BSale />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <UnifiedSalesHistory />
      </TabPanel>
    </Box>
  );
};

export default B2BSalesPage;
